/**
 * Hook that manages product filter state via URL query parameters.
 *
 * Reads/writes filter values through nuqs so every combination of
 * active filters produces a shareable, bookmarkable URL.
 */
import { useQueryStates } from "nuqs";
import { useMemo } from "react";
import {
  
  buildProductFilters,
  filterParsers,
  getVisibleFilters,
  hasActiveFilters
} from "@modules/shop/components/Products/filter-params";
import type {FilterParams} from "@modules/shop/components/Products/filter-params";

const NUQS_OPTIONS = { history: "push", shallow: true } as const;

export function useProductFilters() {
  const [filters, setFilters] = useQueryStates(filterParsers, NUQS_OPTIONS);

  const productFilters = useMemo(
    () => buildProductFilters(filters as FilterParams),
    [filters]
  );

  const visibleFilters = useMemo(
    () => getVisibleFilters((filters as FilterParams).type),
    [filters]
  );

  const isFiltered = useMemo(
    () => hasActiveFilters(filters as FilterParams),
    [filters]
  );

  const resetFilters = () =>
    setFilters({
      type: [],
      color: [],
      size: [],
      minPrice: 0,
      maxPrice: 0,
      gender: "",
    });

  const setType = (value: Array<string>) => setFilters({ type: value });
  const setColor = (value: Array<string>) => setFilters({ color: value });
  const setSize = (value: Array<string>) => setFilters({ size: value });
  const setPriceRange = (min: number, max: number) =>
    setFilters({ minPrice: min, maxPrice: max });
  const setGender = (value: string) => setFilters({ gender: value });

  const toggleType = (value: string) => {
    const current = (filters as FilterParams).type;
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setType(next);
  };

  const toggleColor = (value: string) => {
    const current = (filters as FilterParams).color;
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setColor(next);
  };

  const toggleSize = (value: string) => {
    const current = (filters as FilterParams).size;
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setSize(next);
  };

  return {
    filters: filters as FilterParams,
    productFilters,
    visibleFilters,
    isFiltered,
    resetFilters,
    setType,
    setColor,
    setSize,
    setPriceRange,
    setGender,
    toggleType,
    toggleColor,
    toggleSize,
  };
}
